import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { Type, TypeVariant } from "@shared/ui/Type";
import { ThemeType } from "@shared/types/style.types";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "link"
  | "link-light"
  | "link-inline"
  | "menu-item";

export type Props = {
  as?: React.ElementType;
  children?: React.ReactNode;
  variant?: ButtonVariant;
  className?: string;
  typeClassName?: string;
  disabled?: boolean;
  destructive?: boolean;
  iconComponent?: React.ComponentType<{ className?: string }>;
  type?: "button" | "submit" | "reset";
  typeVariant?: TypeVariant;
  theme?: ThemeType;
  layout?: "vertical" | "horizontal";
  iconPosition?: "left" | "right";
  [key: string]: any;
};

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  (
    {
      as = "button",
      children,
      variant = "primary",
      className,
      typeClassName,
      disabled,
      destructive,
      iconComponent,
      iconPosition = "left",
      theme = "light",
      type = "button",
      typeVariant,
      layout = "horizontal",
      ...props
    },
    ref
  ) => {
    const styles = [
      // default styles
      "flex items-center justify-center cursor-pointer p-2 rounded-full transition-all duration-100",
      "flex-grow-0 whitespace-nowrap",

      // layout styles
      layout === "vertical" && "flex-col",
      layout === "horizontal" && "flex-row",

      // primary light styles
      variant === "primary" &&
        theme === "light" &&
        "bg-brand-800 text-brand-50 border border-brand-800 hover:bg-brand-900 hover:text-brand-100",

      // primary dark styles
      variant === "primary" &&
        theme === "dark" &&
        "bg-brand-50 text-brand-800 border border-brand-50 hover:bg-brand-100 hover:border-brand-100",

      // secondary light styles
      variant === "secondary" &&
        theme === "light" &&
        "bg-brand-50 text-brand-800 border border-brand-800 hover:bg-brand-100",

      // secondary dark styles
      variant === "secondary" &&
        theme === "dark" &&
        "bg-brand-800 text-brand-50 border border-brand-50 hover:bg-brand-700",

      // ghost light styles
      variant === "ghost" &&
        theme === "light" &&
        "bg-transparent text-brand-800 border border-transparent hover:bg-brand-100 hover:border-brand-100",

      // ghost dark styles
      variant === "ghost" &&
        theme === "dark" &&
        "bg-transparent text-brand-50 border border-transparent hover:bg-brand-800 hover:border-brand-700",

      // link light styles
      (variant === "link" ||
        variant === "link-light" ||
        variant === "link-inline") &&
        theme === "light" &&
        "bg-transparent text-brand-800 hover:text-brand-600",

      // link dark styles
      (variant === "link" ||
        variant === "link-light" ||
        variant === "link-inline") &&
        theme === "dark" &&
        "bg-transparent text-brand-50 hover:text-brand-100",

      // link inline styles
      variant === "link-inline" && "p-0 justify-start w-fit gap-2",

      // menu item light styles
      variant === "menu-item" &&
        theme === "light" &&
        "text-left py-4 bg-transparent text-brand-800 rounded-none hover:bg-brand-100",

      // menu item dark styles
      variant === "menu-item" &&
        theme === "dark" &&
        "text-left py-4 bg-transparent text-brand-50 rounded-none hover:bg-brand-700",

      // destructive light styles
      destructive &&
        "bg-error text-brand-50 border-error hover:bg-brand-800 hover:border-brand-800",

      // destructive dark styles
      destructive &&
        theme === "dark" &&
        "bg-error-700 text-brand-50 border-error-700 hover:bg-error-800 hover:border-error-800",

      // dark theme ring styles
      theme === "dark" && "with-ring-inverse",

      // disabled styles
      disabled &&
        "bg-brand-100 text-brand-300 border-brand-300 hover:bg-brand-100 hover:border-brand-300 hover:text-brand-300 cursor-not-allowed",

      // dark theme ghost disabled styles
      theme === "dark" &&
        disabled &&
        variant === "ghost" &&
        "bg-brand-800 border-brand-700 text-brand-700 hover:bg-brand-800 hover:border-brand-700 hover:text-brand-700",
    ];

    const Component = as ? as : "button";

    const buttonTypeVariant = typeVariant || "h6";

    const Icon = iconComponent;

    return (
      <Component
        className={twMerge(
          "group transition-all duration-100 with-ring",
          styles,
          iconPosition === "right" && "flex-row-reverse",
          className
        )}
        type={type}
        disabled={disabled}
        ref={ref as any}
        data-testid={props["data-testid"]}
        {...props}
      >
        {Icon && (
          <Icon
            className={twMerge(
              "h-4 w-4 transition-all duration-100 shrink-0",
              children && "ml-1 mt-0.5"
            )}
          />
        )}
        {children && (
          <Type
            className={twMerge(
              "text-left",
              variant !== "link-inline" && "px-2",
              typeClassName
            )}
            as="span"
            variant={buttonTypeVariant}
          >
            {children}
          </Type>
        )}
      </Component>
    );
  }
);

export default Button;
